
import React, { useState, useEffect, useImperativeHandle, forwardRef } from "react";
import PropTypes from "prop-types";
import { Grid } from "@mui/material";
import { useFormik } from "formik";
import * as yup from "yup";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useHistory } from "react-router-dom";
import TextFieldInput from "../../../components/TextFieldInput";
import SelectInput from "../../../components/SelectInput";
import BoxCheckInput from "../../../components/BoxCheckInput";
import UploadInput from "../../../components/UploadInput";
import CommonButtonVoucherForm from "../../../shared-components/common-button/CommonButtonVoucherForm";
import UploadPopup from "../../../shared-components/upload-popup/UploadPopup";
import {
  setActiveStep,
  setApplicantActiveStep,
  setApplicantActiveStep1,
  setApplicantActiveStep2,
  setEditPreviewApplicant,
  setShowUploadProfileImage,
  setStepsCompleted,
  updateApplicantDetailsForm,
} from "../voucher-form.slice";
import { route } from "../../../services/apiRoutes";
import { COMPARE_VALUE, DATE_START_YEAR, MARITAL_STATUS, MinorAge } from "../../../config/constant";
import SnackbarAlert from "../../../shared-components/snackbar-alert/SnackbarAlert";
import { getTotalYears, isValidPhoneNumberWithRules, removeBlobImages } from "../../../utilis/common-funtions";
import CustomDatePicker from "../../../components/CustomDatePicker";
import { RHFPhoneInput } from "../../../shared-components/phone-input/RHFPhoneInput";
import { fetchCountries } from "../../../shared-components/phone-input/phone-input-slice";
import { useTranslation } from "react-i18next";
import ConfirmationModal from "../../../shared-components/confirmation-modal/ConfirmationModal";
import { useVoucherFormMode } from "../../../utilis/use-voucher-form-mode";

const PersonalDetails = forwardRef(({ applicantNo }, ref) => {
  const dispatch = useDispatch();
  const { voucherId } = useParams();
  const history = useHistory();
  const { isApplicantUpdateMode } = useVoucherFormMode();
  // In the "Update Applicant Info" flow, the primary applicant's identity
  // and contact identifiers must remain locked. Only render-time guard.
  const isPrimaryLockedField = isApplicantUpdateMode && applicantNo === 1;
  const { t } = useTranslation();
  const { countryList } = useSelector((state) => state?.countries);
  const COUNTRY = countryList?.map((c) => ({
    value: c.countryName,
    name: c.countryName,
  }))
  const [snackbarConfig, setSnackbarConfig] = useState({
    open: false,
    message: "",
    severity: "",
  });
  const [dobWarningModal, setDobWarningModal] = useState(false);

  const {
    activeStep,
    masterData,
    showUploadProfileImage,
    applicantData,
    applicantActiveStep,
    stepsCompleted,
  } = useSelector((state) => state.voucherForm);

  const shouldShowMaritalStatus = () => {
    const isSpouse = applicantData?.relationBtApplicants === COMPARE_VALUE.Spouse;
    const isFillingAs = applicantNo === applicantData?.fillingAs;
    const isSecondApplicant = applicantData?.noOfApplicants === 2 && applicantNo === 2;

    return !(isSpouse && (applicantNo === 1 || isFillingAs || isSecondApplicant));
  };
  const personalDetailFormik = useFormik({
    initialValues: {
      salutation: "",
      firstName: "",
      lastName: "",
      countryCode: "IN",
      alternateCountryCode: "IN",
      contactNumber: "",
      alternateContactNumber: "",
      emailAddress: "",
      gender: "",
      residentStatus: "",
      nriCountry: "",
      relation: "",
      relativeName: "",
      dob: "",
      anniversaryDate: "",
      maritalStatus: "",
      image: [],
      isPhysicalImage: false,
    },
    validationSchema: yup.object().shape({
      salutation: yup.string().required("Salutation is required"),
      firstName: yup.string().required("First Name is required"),
      lastName: yup.string().required("Last Name is required"),
      contactNumber: yup
        .string()
        .required("Contact number is required")
        .test(
          "is-valid-phone",
          "Contact number is invalid",
          (value, context) => {
            const { contactNumber, countryCode } = context.parent;
            return isValidPhoneNumberWithRules(countryCode, contactNumber);
          }
        ),
      countryCode: yup.string().required("Country Code is required"),
      alternateContactNumber: yup
        .string()
        .notRequired()
        .test(
          "is-valid-phone",
          "Alternate Contact Number is invalid",
          (value, context) => {
            const { alternateCountryCode } = context.parent;
            if (!value) return true; // allow empty input
            return isValidPhoneNumberWithRules(alternateCountryCode, value);
          }
        )
        .test(
          "not-same-as-contact",
          "Alternate Contact Number should be different from the contact number",
          (value, context) => {
            const { contactNumber } = context.parent;
            if (!value) return true; // allow empty input
            return value !== contactNumber;
          }
        ),
      alternateCountryCode: yup.string().notRequired(),
      emailAddress: yup
        .string()
        .matches(
          /^[\w.%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/,
          "Please enter a valid email address"
        )
        .required("Email Address is required"),
      gender: yup.string().required("Gender is required"),
      residentStatus: yup.string().required("Residential Status is required"),
      nriCountry: yup.string().when("residentStatus", {
        is: "NRI",
        then: () => yup.string().required("Country of Residence is required"),
        otherwise: () => yup.string().notRequired(),
      }),
      relation: yup.string().required("Relation is required"),
      relativeName: yup.string().required("Relation with Name is required"),
      dob: yup.date().typeError("Date is invalid").required("Date of Birth is required"),
      maritalStatus: yup.string().required("Marital Status is required"),
      isPhysicalImage: yup.boolean(),
      image: yup.array().when("isPhysicalImage", {
        is: false,
        then: () =>
          yup
            .array()
            .min(1, "Please upload profile photo")
            .required("Profile photo is required"),
        otherwise: () => yup.array().notRequired(),
      }),
      // anniversaryDate: yup.string().when("maritalStatus", {
      //   is: (maritalStatus) => maritalStatus === MARITAL_STATUS.MARRIED,
      //   then: () =>
      //     yup
      //       .string()
      //       .required("Anniversary Date is required")
      //       .test(
      //         "is-after-dob",
      //         "Anniversary Date cannot be before Date of Birth",
      //         function (value) {
      //           const { dob } = this.parent;
      //           if (!value || !dob) return true; // Skip if either missing
      //           const anniversary = new Date(value);
      //           const birthDate = new Date(dob);
      //           return anniversary >= birthDate;
      //         }
      //       ),
      //   otherwise: () => yup.string().notRequired(),
      // }),
    }),
  });

  const handleSnackbarClose = () => {
    setSnackbarConfig({
      open: false,
      message: "",
      severity: "",
    });
  };

  useEffect(() => {
    dispatch(fetchCountries())
  }, [])

  useEffect(() => {
    if (
      applicantData?.relationBtApplicants === COMPARE_VALUE.Spouse &&
      applicantNo === 1
    ) {
      personalDetailFormik.setFieldValue("maritalStatus", MARITAL_STATUS.MARRIED);
    }
    if (applicantData[`applicant${applicantNo}`] !== null) {
      const applicantDetails = applicantData[`applicant${applicantNo}`];
      const personalDetails = applicantDetails?.personalDetails;

      // Merge personal details with contact details for form values
      const mergedValues = {
        ...personalDetailFormik.initialValues,
        ...personalDetails,
        // Map contact details to form field names
      };

      personalDetailFormik.setValues(mergedValues);
    }
    if (applicantData?.relationBtApplicants === COMPARE_VALUE.Spouse && applicantNo === applicantData?.fillingAs) {
      if (applicantNo === 2) {
        personalDetailFormik.setFieldValue("maritalStatus", MARITAL_STATUS.MARRIED);
      }
    }
    if (applicantData?.relationBtApplicants === COMPARE_VALUE.Spouse && applicantData?.noOfApplicants === 2) {
      if (applicantNo === 2) {
        personalDetailFormik.setFieldValue("maritalStatus", MARITAL_STATUS.MARRIED);
      }
    }

    if (applicantData?.relationBtApplicants === COMPARE_VALUE.Spouse && applicantData?.fillingAs === 1 && applicantNo === 2) {
      if (applicantNo === 2) {
        personalDetailFormik.setFieldValue("maritalStatus", MARITAL_STATUS.MARRIED);
      }
    }

    return () => { };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applicantData, applicantNo]);

  useEffect(() => {
    personalDetailFormik.validateForm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [personalDetailFormik?.values]);

  // Effect to handle anniversary date validation when marital status changes
  useEffect(() => {
    if (
      personalDetailFormik?.values?.maritalStatus !== MARITAL_STATUS.MARRIED &&
      personalDetailFormik?.values?.anniversaryDate
    ) {
      // Only clear anniversary date if marital status is not married AND there's an existing anniversary date
      // This prevents clearing the date when loading saved data
      personalDetailFormik.setFieldValue("anniversaryDate", "");
      personalDetailFormik.setFieldTouched("anniversaryDate", false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [personalDetailFormik?.values?.maritalStatus]);

  const applicantDetails = applicantData[`applicant${applicantNo}`]?.personalDetails;
  useEffect(() => {
    if (personalDetailFormik?.values?.residentStatus === "NRI") {
      personalDetailFormik.setFieldValue(
        "nriCountry",
        applicantDetails?.nriCountry
      );
    } else {
      personalDetailFormik.setFieldValue("nriCountry", "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applicantDetails, applicantNo, personalDetailFormik?.values?.residentStatus])

  const handleRelationChange = (e) => {
    const relation = e.target.value;
    personalDetailFormik.setFieldValue("relation", relation);

    if (relation === "W/o" || relation === "H/o") {
      personalDetailFormik.setFieldValue("maritalStatus", MARITAL_STATUS.MARRIED);
    } else if (relation === "S/o" || relation === "D/o") {
      personalDetailFormik.setFieldValue("maritalStatus", ""); // Clear selection
    }
  };

  const saveAndPreview = async (type) => {
    if (Object.keys(personalDetailFormik.errors).length === 0) {
      const payload = {
        ...createPersonalDetailsPayload(type === "saveForLater"),
        lastStep: Math.max(applicantData?.lastStep, activeStep),
      };
      await dispatch(
        updateApplicantDetailsForm({
          path: `${route.UPDATE_VOUCHER_APPLICANT_DETAILS}/${voucherId}`,
          payload: payload,
          opportunityId: voucherId
        })
      )
        .unwrap()
        .then((res) => {
          if (res?.status === 200) {
            dispatch(setEditPreviewApplicant(false));
            history.push(
              isApplicantUpdateMode
                ? `/voucher-form/thank-you/${voucherId}`
                : `/customer-preview/${voucherId}`
            );

            let completedSteps = {
              ...stepsCompleted,
              [activeStep]: true,
            };
            dispatch(setStepsCompleted(completedSteps));
            dispatch(setApplicantActiveStep(0));

          }
        });
    }

  }
  // Expose the save function to the parent component
  useImperativeHandle(ref, () => ({
    handleSaveForm,
    errors: personalDetailFormik.errors,
  }));

  // Helper function to create the personal details payload
  const createPersonalDetailsPayload = (saveForLaterFlag = false) => {
    return {
      applicantNumber: Number.parseInt(applicantNo),
      saveForLater: saveForLaterFlag,
      personalDetails: {
        salutation: personalDetailFormik?.values?.salutation,
        firstName: personalDetailFormik?.values?.firstName,
        lastName: personalDetailFormik?.values?.lastName,
        contactNumber: personalDetailFormik?.values?.contactNumber,
        countryCode: personalDetailFormik?.values?.countryCode,
        alternateContactNumber: personalDetailFormik?.values?.alternateContactNumber,
        alternateCountryCode: personalDetailFormik?.values?.alternateCountryCode,
        emailAddress: personalDetailFormik?.values?.emailAddress,
        gender: personalDetailFormik?.values?.gender,
        residentStatus: personalDetailFormik?.values?.residentStatus,
        nriCountry: personalDetailFormik?.values?.nriCountry,
        relation: personalDetailFormik?.values?.relation,
        relativeName: personalDetailFormik?.values?.relativeName,
        dob: personalDetailFormik?.values?.dob,
        // anniversaryDate: personalDetailFormik?.values?.anniversaryDate,
        image: personalDetailFormik?.values?.image,
        maritalStatus: personalDetailFormik?.values?.maritalStatus,
        isPhysicalImage: personalDetailFormik?.values?.isPhysicalImage,
      },
    };
  };

  const handleSaveForm = (type) => {
    if (Object.keys(personalDetailFormik.errors).length === 0) {
      const payload = {
        ...createPersonalDetailsPayload(type === "saveForLater"),
      };

      dispatch(
        updateApplicantDetailsForm({
          path: `${route.UPDATE_VOUCHER_APPLICANT_DETAILS}/${voucherId}`,
          payload: payload,
          opportunityId: voucherId
        })
      );
    }
  };
  const handleOnChange = (e, index, key, list) => {
    let selectedValue = list[index]?.value;
    personalDetailFormik.setFieldValue(key, selectedValue);
  };

  const handleSalutationChange = (e, index, key, list) => {
    if (key === "salutation") {
      // First set the salutation value
      personalDetailFormik.setFieldValue(key, e.target.value);

      // Get the selected option from masterData
      const selectedOption = masterData?.Salutation?.find(
        (option) => option.value === e.target.value
      );

      if (selectedOption) {
        const selectedName = selectedOption.name?.toLowerCase();

        if (selectedName === "mr" || selectedName === "mr.") {
          // Find the male option in gender master data
          const maleOption = masterData?.GenderIdentity?.find(
            (option) => option.name?.toLowerCase() === "male"
          );
          if (maleOption) {
            personalDetailFormik.setFieldValue("gender", maleOption.value);
          }
        } else if (
          selectedName === "ms" ||
          selectedName === "ms." ||
          selectedName === "mrs" ||
          selectedName === "mrs."
        ) {
          // Find the female option in gender master data
          const femaleOption = masterData?.GenderIdentity?.find(
            (option) => option.name?.toLowerCase() === "female"
          );
          if (femaleOption) {
            personalDetailFormik.setFieldValue("gender", femaleOption.value);
          }
        } else {
          personalDetailFormik.setFieldValue("gender", "");
        }
      }
    }
  };
  const handleShowUploadProfileImage = () => {
    removeBlobImages();
    dispatch(setShowUploadProfileImage(!showUploadProfileImage));
  };

  const handlePrevious = () => {
    dispatch(setActiveStep(activeStep - 1));
  };

  const handleImageValue = (keyName, imgName) => {
    removeBlobImages();
    personalDetailFormik.setFieldValue(keyName, imgName);
  };

  const handlePhysicalImage = (fieldName, value) => {
    if (fieldName === "image") {
      personalDetailFormik.setFieldValue("isPhysicalImage", value);
    }
  };

  const handleUpdateImageData = (fieldName, imageData) => {
    personalDetailFormik.setFieldValue(fieldName, imageData);
  };

  useEffect(() => {
    if (applicantNo === 1) {
      dispatch(setApplicantActiveStep1(applicantActiveStep));
    }

    if (applicantNo === 2) {
      dispatch(setApplicantActiveStep2(applicantActiveStep));
    }

    return () => { };
  }, [applicantActiveStep, applicantNo, dispatch]);

  useEffect(() => {
    if (applicantNo === 1) {
      dispatch(setApplicantActiveStep1(applicantActiveStep));
    }

    if (applicantNo === 2) {
      dispatch(setApplicantActiveStep2(applicantActiveStep));
    }

    return () => { };
  }, [applicantActiveStep, applicantNo, dispatch]);
  const handleCloseDOBwarningModal = () => {
    personalDetailFormik.setFieldValue("dob", null);
    setDobWarningModal(false);
  };
  return (
    <div>
      <ConfirmationModal
        open={dobWarningModal}
        onClose={handleCloseDOBwarningModal}
        onConfirm={handleCloseDOBwarningModal}
        title="Age Restriction Notice"
        message="Primary applicant must be an adult. Please update the date of birth."
        confirmText="OK"
        disableBackdropClick={true}
        hideCloseIcon={true}
      />
      <form
        onSubmit={personalDetailFormik.handleSubmit}
        onReset={personalDetailFormik.handleReset}
        noValidate
      >
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={6} lg={6} xl={6}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={6} lg={4} xl={4}>
                <div className="salutationWidth">
                  <SelectInput
                    field={{
                      name: "salutation",
                      label: "Salutation",
                      required: true,
                      readonly: false,
                      options: masterData?.Salutation,
                      placeholder: "Select",
                      handleOnChange: handleSalutationChange, // 👈 pass the custom handler here
                    }}
                    formik={personalDetailFormik}
                  />
                </div>
              </Grid>
              <Grid item xs={12} sm={6} md={6} lg={8} xl={8}>
                <div>
                  <TextFieldInput
                    field={{
                      name: "firstName",
                      label: "First Name",
                      required: true,
                      readonly: isPrimaryLockedField,
                      placeholder: "Enter First Name",
                      handleOnChange: (e) => {
                        const value = e.target.value;
                        const newValue = value?.replaceAll(/[^a-zA-Z\s]/g, "");
                        personalDetailFormik.setFieldValue(
                          "firstName",
                          newValue
                        );
                      },
                    }}
                    formik={personalDetailFormik}
                  />
                </div>
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12} sm={6} md={6} lg={6} xl={6}>
            <TextFieldInput
              field={{
                name: "lastName",
                label: "Last Name",
                placeholder: "Enter Last Name",
                required: true,
                readonly: isPrimaryLockedField,
                handleOnChange: (e) => {
                  const value = e.target.value;
                  const newValue = value?.replaceAll(/[^a-zA-Z]/g, "");
                  personalDetailFormik.setFieldValue(
                    "lastName",
                    newValue
                  );
                }
              }}
              formik={personalDetailFormik}
            />
          </Grid>
        </Grid>
        <Grid container spacing={2} mt={0}>
          <Grid item xs={12} sm={6} md={6} lg={6} xl={6}>
            <RHFPhoneInput
              name="contactNumber"
              countryCodeName="countryCode"
              label="Contact Number"
              country={personalDetailFormik?.values?.countryCode || "IN"}
              formik={personalDetailFormik}
              placeholder="Enter Contact Number"
              required
              showDialCode={true}
              disabled={isPrimaryLockedField}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={6} lg={6} xl={6}>
            <RHFPhoneInput
              name="alternateContactNumber"
              countryCodeName="alternateCountryCode"
              label="Alternate Contact Number"
              country={personalDetailFormik?.values?.alternateCountryCode || "IN"}
              formik={personalDetailFormik}
              placeholder="Enter Alternate Contact Number"
              showDialCode={true}
            />
          </Grid>
        </Grid>

        <Grid container spacing={2} mt={0}>
          <Grid item xs={12} sm={6} md={6} lg={6} xl={6}>
            <TextFieldInput
              field={{
                name: "emailAddress",
                label: "Email",
                placeholder: "Enter Email",
                required: true,
                readonly: isPrimaryLockedField,
                maxLength: 50,
                value: personalDetailFormik?.values?.emailAddress,
              }}
              formik={personalDetailFormik}
            />
          </Grid>
        </Grid>

        <Grid container spacing={2} mt={0}>
          <Grid item xs={12} sm={6} md={6} lg={6} xl={6}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={6} lg={4} xl={4}>
                <div className="salutationWidth">
                  <SelectInput
                    field={{
                      name: "relation",
                      label: "Relation",
                      required: true,
                      readonly: false,
                      options: masterData?.Identity, //IDENTITY,
                      placeholder: "Select",
                      handleOnChange: handleRelationChange,
                    }}
                    formik={personalDetailFormik}
                  />
                </div>
              </Grid>
              <Grid item xs={12} sm={6} md={6} lg={8} xl={8}>
                <div>
                  <TextFieldInput
                    field={{
                      name: "relativeName",
                      placeholder: "Enter Name",
                      label: "Enter Name",
                      required: true,
                      readonly: false,
                    }}
                    formik={personalDetailFormik}
                  />
                </div>
              </Grid>
            </Grid>
          </Grid>

          <Grid item xs={12} sm={6} md={6} lg={6} xl={6}>
            <BoxCheckInput
              field={{
                name: "gender",
                label: "Gender",
                required: true,
                readonly: false,
                options: masterData?.GenderIdentity,
                grid: true,
                xsCol: 4,
                smCol: 4,
                mdCol: 4,
                lgCol: 4,
                handleOnChange,
              }}
              formik={personalDetailFormik}
            />
          </Grid>
        </Grid>

        {/* 2 */}
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={6} lg={6} xl={6}>
            <CustomDatePicker
              label="Date of Birth"
              value={personalDetailFormik?.values?.dob}
              onChange={(date) => {
                if (date?.target?.value) {
                  const actualAge = getTotalYears(date?.target?.value);
                  if (actualAge < MinorAge && applicantNo === 1) {
                    setDobWarningModal(true);
                  }
                  // Update Formik value
                  personalDetailFormik.setFieldValue("dob", date.target.value);
                }
              }}
              name="dob"
              maxDate={new Date()}
              minDate={new Date(DATE_START_YEAR, 0, 1)}
              error={
                personalDetailFormik.touched.dob &&
                Boolean(personalDetailFormik.errors.dob)
              }
              helperText={
                personalDetailFormik.touched.dob &&
                personalDetailFormik.errors.dob
              }
              required={true}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={6} lg={6} xl={6} mt={0.3}>
            <BoxCheckInput
              field={{
                name: "residentStatus",
                label: "Residential Status",
                required: true,
                readonly: false,
                options: masterData?.Resident_Status,
                grid: true,
                xsCol: 4,
                smCol: 4,
                mdCol: 4,
                lgCol: 4,
                handleOnChange,
              }}
              formik={personalDetailFormik}
            />
          </Grid>
        </Grid>
        {/* 3 */}
        <Grid container spacing={2} mt={0}>
          {personalDetailFormik.values.residentStatus === "NRI" && (
            <Grid item xs={12} sm={6} md={6} lg={6} xl={6}>
              <SelectInput
                field={{
                  name: "nriCountry",
                  label: "Country of Residence",
                  required: true,
                  readonly: false,
                  options: COUNTRY,
                  placeholder: "Select",
                }}
                formik={personalDetailFormik}
              />
            </Grid>
          )}
          {shouldShowMaritalStatus() && (
            <Grid item xs={12} sm={6} md={6} lg={6} xl={6}>
              <BoxCheckInput
                field={{
                  name: "maritalStatus",
                  label: "Marital Status",
                  required: true,
                  readonly: false,
                  options: masterData?.Marital_Status,
                  grid: true,
                  xsCol: 4,
                  smCol: 4,
                  mdCol: 4,
                  lgCol: 4,
                  handleOnChange,
                }}
                formik={personalDetailFormik}
              />
            </Grid>
          )}

          <Grid item xs={12} sm={6} md={6} lg={6} xl={6}>
            <UploadInput
              field={{
                name: "image",
                label: t("commonLabels.passportSizePhoto"),
                placeholder: personalDetailFormik?.values?.isPhysicalImage
                  ? "Upload Later"
                  : "Upload Passport Size Photo",
                placeholderUpload: "Photo uploaded",
                required: true,
                readonly: false,
                handleOnClick: handleShowUploadProfileImage,
                physicalCopyValue:
                  personalDetailFormik?.values?.isPhysicalImage,
              }}
              formik={personalDetailFormik}
            />
          </Grid>
        </Grid>

        {showUploadProfileImage && (
          <UploadPopup
            heading="Upload Your Recent Passport Size Photo"
            totalImages={1}
            closePopup={handleShowUploadProfileImage}
            handleImageValue={handleImageValue}
            uploadedImage={personalDetailFormik?.values?.image}
            fieldName={"image"}
            handleUpdateImageData={handleUpdateImageData}
            handlePhysicalImage={handlePhysicalImage}
            selectedOptionValue={personalDetailFormik?.values?.isPhysicalImage}
          />
        )}
        <CommonButtonVoucherForm
          handlePrevious={handlePrevious}
          handleNext={handleSaveForm}
          innerSteps={true}
          saveAndPreview={saveAndPreview}
          applicantNo={applicantNo}
          showPrevBtn={activeStep === 0}
          hidePrevButton={activeStep === 0}
          saveDraftData={{
            ...createPersonalDetailsPayload(true),
            lastStep: Math.max(applicantData?.lastStep, activeStep),
          }}
        />
        <SnackbarAlert
          open={snackbarConfig.open}
          message={snackbarConfig.message}
          severity={snackbarConfig.severity}
          onClose={handleSnackbarClose}
        />
      </form>
    </div>
  );
});



PersonalDetails.propTypes = {
  applicantNo: PropTypes.any,
};

export default PersonalDetails;
